import { combineReducers } from 'redux';
import { getType } from 'typesafe-actions';
import { actions } from '..';
import { orderBy } from '../../data-utils';
import { getKey, VoiceMail, VoiceMailFolder, Identifier } from '../../model';
import { Actions, StoreState } from '../types';
import produce from 'immer';

type Folders = Record<VoiceMailFolder, VoiceMail[]>

const EmptyFolders: Folders = {
  'Inbox': [],
  'Sent': [],
  'Saved': [],
  'Future': [],
  'Deleted': [],
}

interface Mailbox {
  [id: string]: Folders
}

function folders(state: Mailbox = {}, action: Actions): Mailbox {
  if (action.type === getType(actions.fetchVoiceMail.response)) {
    const { mailbox, folder, items } = action.payload
    const id = getKey(mailbox)
    const data = state[id]

    if (!data) {
      const d = { ...EmptyFolders, [folder]: items }
      return {
        ...state,
        [getKey(mailbox)]: d,
      }
    } else {
      return {
        ...state,
        [getKey(mailbox)]: { ...data, [folder]: items },
      }
    }
  }

  if (action.type === getType(actions.deleteVoiceMail)) {
    const { mailbox, id, folder } = action.payload
    const key = getKey(mailbox)
    const vm = state[key][folder].filter(vm => vm.id === id)[0]
    if (!vm) { return state }

    const data = state[key]
    return {
      ...state,
      [key]: { ...data, [folder]: data[folder].filter(vm => vm.id !== id) },
    }
  }

  if (action.type === getType(actions.updateVmMessage)) {
    const { mailbox, id, type, status, urgent, date, personal, subject, duration, number, folder, person } = action.payload
    const key = getKey(mailbox)
    const data = state[key]
    if (data) {
      const message = data[folder].filter(vm => vm.id === id)[0]
      if (message) {
        const messages = data[folder].filter(vm => vm.id !== id)
        return {
          ...state,
          [key]: { ...data, [folder]: [...messages, { ...message, status }].sort(orderBy<VoiceMail>(vm => vm.received).desc()) },
        }

      } else {
        const messages = data[folder].filter(vm => vm.id !== id)
        const message = { id, caller: { id: number, name: person }, folder, type, status, urgent, received: date, personal, subject, duration } as VoiceMail
        return {
          ...state,
          [key]: { ...data, [folder]: [message, ...messages] },
        }
      }
    }  

    return state
  }

  if (action.type === getType(actions.forceReload)) {
    return {}
  }

  if (action.type === getType(actions.removeVoicemail)) {
    const newState = { ...state }
    delete newState[getKey(action.payload)]
    return newState
  }

  return state
}

function getValidKey(mailbox: Identifier, folder: VoiceMailFolder) {
  return getKey(mailbox) + folder
}

type UnreadCount = Partial<Record<string, number>>

function unread(state: UnreadCount = {}, action: Actions): UnreadCount {
  if (action.type === getType(actions.updateVmUnreadCount)) {
    const key = getKey(action.payload.mailbox)
    return {
      ...state,
      [key]: action.payload.count,
    }
  }

  if (action.type === getType(actions.removeVoicemail)) {
    const newState = { ...state }
    delete newState[getKey(action.payload)]
    return newState
  }

  return state
}

type Valid = Partial<Record<string, boolean>>

function valid(state: Valid = {}, action: Actions): Valid {
  if (action.type === getType(actions.fetchVoiceMail.response)) {
    const { mailbox, folder } = action.payload
    const id = getValidKey(mailbox, folder)
    return {
      ...state,
      [id]: true,
    }
  }

  if (action.type === getType(actions.removeVoicemail)) {
    return produce(state, state => {
      const id = getKey(action.payload)
      for (const key in state) {
        if (key.indexOf(id) === 0) {
          if (state.hasOwnProperty(key)) {
            delete state[key]
          }
        }
      }
    })
  }


  if (action.type === getType(actions.forceReload)) {
    return {}
  }

  return state
}

type PasswordSet = Partial<Record<string, boolean>>

function passwordSet(state: Valid = {}, action: Actions): PasswordSet {
  if (action.type === getType(actions.removeVoicemail)) {
    const newState = { ...state }
    delete newState[getKey(action.payload)]
    return newState
  }

  if (action.type === getType(actions.setHaveVmPin)) {
    const id = getKey(action.payload)
    return {
      ...state,
      [id]: true,
    }
  }

  return state
}

export default combineReducers({
  folders,
  unread,
  valid,
  passwordSet,
})

function getRoot(state: StoreState) { return state.voiceMail }
function getFoldersByExtension(state: StoreState) { return getRoot(state).folders }
function getUnreadCountByExtension(state: StoreState) { return getRoot(state).unread }
function getValidFolderByExtension(state: StoreState) { return getRoot(state).valid }
function getPaswordSetByExtension(state: StoreState) { return getRoot(state).passwordSet }

export function selectVoiceMailFolder(state: StoreState, extension: Identifier, folder: VoiceMailFolder) {
  const id = getKey(extension)
  const data = getFoldersByExtension(state)[id]
  if (!data) {
    return EmptyFolders[folder]
  } else {
    return data[folder]
  }
}

export function selectVoiceMail(state: StoreState, id: string) {
  const data = getRoot(state)
  if (data) {
    for (const key in data.folders) {
      if (Object.prototype.hasOwnProperty.call(data.folders, key)) {
        const folders = data.folders[key];

        for (const voiceMail of folders.Inbox) {
          if (voiceMail.id === id) {
            return voiceMail
          }
        }

        for (const voiceMail of folders.Deleted) {
          if (voiceMail.id === id) {
            return voiceMail
          }
        }

        for (const voiceMail of folders.Future) {
          if (voiceMail.id === id) {
            return voiceMail
          }
        }

        for (const voiceMail of folders.Saved) {
          if (voiceMail.id === id) {
            return voiceMail
          }
        }

        for (const voiceMail of folders.Sent) {
          if (voiceMail.id === id) {
            return voiceMail
          }
        }
      }
    }
  }

  return null
}

export function shouldFetchVoiceMail(state: StoreState, extension: Identifier, folder: VoiceMailFolder) {
  const id = getValidKey(extension, folder)
  return !getValidFolderByExtension(state)[id]
}

export function selectUnreadVmCount(state: StoreState, extension: Identifier) {
  const id = getKey(extension)
  return getUnreadCountByExtension(state)[id] || 0
}

export function selectVmPinRequired(state: StoreState, extension: Identifier) {
  const id = getKey(extension)
  return getPaswordSetByExtension(state)[id] === undefined
}
